/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import { renderBreakingNews, renderComponent, renderIsomorphicComponent } from "@quintype/framework/client/start";
import get from "lodash/get";
import WithLazyTrigger from "../isomorphic/components/with-lazy-trigger";
import { AmazonPollyPopup } from "../isomorphic/components/amazon-polly/amazon-polly-popup";
import DFPHelper from "../isomorphic/components/atoms/dfp-helper";
import { MobileDownloadLinks } from "../isomorphic/components/atoms/mobile-apps";
import { LoadingIndicatorComponent } from "../isomorphic/components/basic/loading-indicator.js";
import { MeteredStoryIndicator } from "../isomorphic/components/basic/metered-story-indicator/metered-story-indicator";
import { BreakingNewsView } from "../isomorphic/components/breaking-news/breaking-news-view";
import { HeaderLogin } from "../isomorphic/components/headers/login";
import { DefaultMenu } from "../isomorphic/components/headers/menu/default-menu";
import { SecondaryMenu } from "../isomorphic/components/headers/menu/secondary-menu";
import { HeaderSearch } from "../isomorphic/components/headers/search";
import { HeaderSidebar } from "../isomorphic/components/headers/sidebar";
import { HeaderSocialFollow } from "../isomorphic/components/headers/social-follow-wrapper";
import { HeaderSticky } from "../isomorphic/components/headers/sticky";
import WithSubsequentLoad from "../isomorphic/components/hoc-components/with-subsequent-load";
import WithArrowConfig from "../isomorphic/components/hoc-components/with-arrow-config";
import { InjectWidget } from "../isomorphic/components/inject-widget";
import { Metype } from "../isomorphic/components/molecules/metype";
import { PBThemeSettings } from "../isomorphic/components/pb-theme";
import { FooterLink } from "../isomorphic/components/rows/footer/link";
import { Menu } from "../isomorphic/components/rows/footer/menu";
import { SocialFollowWrapper } from "../isomorphic/components/rows/footer/social-follow-wrapper";
import { SSOWrapper } from "../isomorphic/components/sso-components/sso-dynamic";
import { WidgetComponent } from "../isomorphic/components/Widget";
import { pickComponent } from "../isomorphic/pick-component";
import { decodeCustomCode } from "../utils/decodeCustomCode";
import { hydrateStory } from "./hydrate-story";
import { AdSlotRow } from "../isomorphic/components/collection-templates/ad-slot-row";
import { CustomWidgetRow } from "../isomorphic/components/collection-templates/custom-widget-row";

export function preRenderApplication(store) {
  const storeConfig = get(store.getState(), ["qt", "config"], {});
  const themeAttributes = storeConfig["theme-attributes"] || {};
  const pbConfig = storeConfig["pagebuilder-config"];
  const footerTheme = get(pbConfig, ["footer", "footerStyle"], get(themeAttributes, ["footer_theme"], "footer_1"));
  const headerTheme = get(pbConfig, ["header", "headerStyle"], get(themeAttributes, ["header_theme"], "header_1"));
  const enableFooterAppDownloadLinks = get(pbConfig, ["footer", "enableAppDownloadLinksButton"], false);
  const enableHeaderAppDownloadLinks = get(pbConfig, ["header", "enableAppDownloadLinksButton"], false);
  const isEnableLiveFeed = get(
    pbConfig,
    ["general", "meType", "enableLiveFeed"],
    get(themeAttributes, ["metype-config", "enableLiveFeed"])
  );

  renderComponent(InjectWidget, "widget", store);
  renderComponent(PBThemeSettings, "pbTheme", store);

  renderComponent(FooterLink, "footer-link", store);

  renderComponent(HeaderSocialFollow, "header-social-follow", store, { hydrate: true });
  renderComponent(HeaderSidebar, "header-sidebar", store, { hydrate: true });
  renderComponent(HeaderSearch, "header-search", store, { hydrate: true });
  renderComponent(DefaultMenu, "header-default-menu", store, { hydrate: true });
  renderComponent(HeaderLogin, "header-login", store, { hydrate: true });
  renderComponent(HeaderSticky, "header-sticky", store, { hydrate: true });

  if (footerTheme !== "footer_1") {
    renderComponent(SocialFollowWrapper, "social-follow-container", store, { hydrate: true });
    renderComponent(Menu, "footer-menu", store, { hydrate: true });

    if (pbConfig && enableFooterAppDownloadLinks) {
      renderComponent(MobileDownloadLinks, "footer-mobile-download-links", store, { hydrate: true });
    }
  }

  if (!headerTheme.includes("single-layer") || headerTheme.includes("single-layer-header_5")) {
    renderComponent(HeaderSearch, "header-search-mobile", store, { hydrate: true });
  }

  if (!headerTheme.includes("single-layer")) {
    renderComponent(HeaderSidebar, "header-sidebar-mobile", store, { hydrate: true });
    renderComponent(SecondaryMenu, "header-secondary-menu", store, { hydrate: true });
    if (pbConfig && enableHeaderAppDownloadLinks) {
      renderComponent(MobileDownloadLinks, "header-mobile-download-links", store, { hydrate: true });
    }
  }

  if (!themeAttributes.hide_breaking_news) renderBreakingNews("breaking-news-container", store, BreakingNewsView);
  if (isEnableLiveFeed) global.setTimeout(() => renderComponent(Metype, "meType", store), 3000);
}

// This is a separate file as everything from here on is hot reloaded when the app changes
export function renderApplication(store) {
  const storeQTData = get(store.getState(), ["qt"], {});
  const storeConfig = get(storeQTData, ["config"], {});
  const pageType = get(storeQTData, ["pageType"], "");
  const pbConfig = storeConfig["pagebuilder-config"];
  const lightRows = get(pbConfig, ["home", "lightRows"], false);
  const themeAttributes = storeConfig["theme-attributes"];
  const footerTheme = get(pbConfig, ["footer", "footerStyle"], get(themeAttributes, ["footer_theme"], "footer_1"));
  const headerTheme = get(pbConfig, ["header", "headerStyle"], get(themeAttributes, ["header_theme"], "header_1"));
  const enableFooterAppDownloadLinks = get(pbConfig, ["footer", "enableAppDownloadLinksButton"], false);
  const enableHeaderAppDownloadLinks = get(pbConfig, ["header", "enableAppDownloadLinksButton"], false);

  renderBreakingNews("breaking-news-container", store, BreakingNewsView);
  renderComponent(LoadingIndicatorComponent, "loading-indicator", store);
  renderComponent(DFPHelper, "dfp-helper", store);
  // TODO: Lazyload SSO
  renderComponent(SSOWrapper, "sso-wrapper", store);
  // TODO: Render only in story page.
  renderComponent(MeteredStoryIndicator, "metered-story-indicator", store);
  renderComponent(AmazonPollyPopup, "amazon-polly", store);

  renderComponent(HeaderSidebar, "header-sidebar", store, { hydrate: true });
  renderComponent(HeaderSocialFollow, "header-social-follow", store, { hydrate: true });
  renderComponent(HeaderSearch, "header-search", store, { hydrate: true });
  renderComponent(DefaultMenu, "header-default-menu", store, { hydrate: true });
  renderComponent(HeaderLogin, "header-login", store, { hydrate: true });
  renderComponent(FooterLink, "footer-link", store);
  renderComponent(HeaderSticky, "header-sticky", store, { hydrate: true });

  if (footerTheme !== "footer_1") {
    renderComponent(SocialFollowWrapper, "social-follow-container", store, { hydrate: true });
    renderComponent(Menu, "footer-menu", store, { hydrate: true });

    if (pbConfig && enableFooterAppDownloadLinks) {
      renderComponent(MobileDownloadLinks, "footer-mobile-download-links", store, { hydrate: true });
    }
  }
  if (!headerTheme.includes("single-layer") || headerTheme.includes("single-layer-header_5")) {
    renderComponent(HeaderSearch, "header-search-mobile", store, { hydrate: true });
  }

  if (!headerTheme.includes("single-layer")) {
    renderComponent(HeaderSidebar, "header-sidebar-mobile", store, { hydrate: true });
    renderComponent(SecondaryMenu, "header-secondary-menu", store, { hydrate: true });
    if (pbConfig && enableHeaderAppDownloadLinks) {
      renderComponent(MobileDownloadLinks, "header-mobile-download-links", store, { hydrate: true });
    }
  }

  const pbConfigRows = get(pbConfig, ["header", "rows"], []);
  pbConfigRows
    .filter(row => row.type === "ads" || row.type === "widget")
    .forEach(filteredRow => {
      renderComponent(
        filteredRow.type === "ads" ? AdSlotRow : CustomWidgetRow,
        `${filteredRow.type}-${filteredRow.rowId}`,
        store,
        { metadata: filteredRow }
      );
    });

  if (global.location.hash === "#secret-mode") {
    import(/* webpackChunkName: "secret-mode" */ "./secret/secret-mode").then(({ startSecretMode }) =>
      startSecretMode(store)
    );
  }

  const isStoryInfiniteScrollEnabled =
    get(pbConfig, ["infiniteScroll", "infiniteScrollType"], get(themeAttributes, ["infinite-scroll"], "disabled")) !==
    "disabled";

  const storyType = get(storeQTData, ["data", "story", "story-template"], "");
  const canSelectivelyHydrateStory = ["text"].includes(storyType);

  // Handle Story Page Selective Hydration
  if (pageType === "story-page" && canSelectivelyHydrateStory && !isStoryInfiniteScrollEnabled) {
    // Selectively hydrate only when qtLoadedFromShellfrom shell is false
    if (!global.qtLoadedFromShell) {
      return hydrateStory(store, storeQTData, pbConfig);
    }
  }

  if (pageType === "home-page" && lightRows) {
    global.qtLoadedFromShell && renderIsomorphicComponent("container", store, pickComponent, { hydrate: true });
  } else {
    return renderIsomorphicComponent("container", store, pickComponent, { hydrate: !global.qtLoadedFromShell });
  }

  if (pageType === "home-page" && lightRows) {
    const collectionItems = get(storeQTData, ["data", "collection", "items"], []);
    const pbConfigRows = get(pbConfig, ["home", "rows"], []);

    /*
      Loop through pb config to check if the collection
      component has load more as subsequent to hydrate it
    */
    pbConfigRows
      .filter(row => row.type === "collections")
      .forEach(({ config = {}, layout }, index) => {
        const collection = collectionItems[index];
        if (collection) {
          const elementId = `ELM-${collection.id}`;
          const timeoutCounter = setTimeout(() => {
            if (global && global.document && global.document.getElementById(elementId)) {
              renderComponent(WithLazyTrigger, elementId, store, {
                onIntersection: async () => {
                  const ExtendedLoadMore = (await import("../isomorphic/components/elm-default-export")).default;
                  renderComponent(ExtendedLoadMore, elementId, store, {
                    config,
                    componentName: layout,
                    WithArrowConfig,
                    withSubsequentLoad: WithSubsequentLoad
                  });
                }
              });
            }
            clearTimeout(timeoutCounter);
          }, 2500);
        }
      });

    /*
      Loop through pb config to selectively hydrate widget rows
    */
    pbConfigRows
      .filter(row => row.type === "widget")
      .forEach(row => {
        const widgetContainerId = `widget-${row.rowId}`;
        if (global && global.document && global.document.getElementById(widgetContainerId)) {
          renderComponent(WidgetComponent, widgetContainerId, store, {
            widgetValue: `<div>${decodeCustomCode(get(row, ["config", "customCode"], ""))}</div>`,
            hydrate: true
          });
        }
      });
  }
}
